import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../config';
import { getAuthFetchOptions } from '../utils/auth';

const STORAGE_KEY = 'cloudcor_system_branding';

const DEFAULT_BRANDING = {
  companyName: '',
  logoData: '',
  showCompanyLogo: true,
  updatedAt: null,
};

const BrandingContext = createContext({
  branding: DEFAULT_BRANDING,
  loading: false,
  refreshBranding: () => Promise.resolve(),
  updateBranding: () => Promise.resolve(),
});

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(() => {
    try {
      const cached = localStorage.getItem(STORAGE_KEY);
      if (cached) {
        return { ...DEFAULT_BRANDING, ...JSON.parse(cached) };
      }
    } catch (_) {}
    return DEFAULT_BRANDING;
  });
  const [loading, setLoading] = useState(true);

  const persistBranding = useCallback((nextBranding) => {
    const merged = { ...DEFAULT_BRANDING, ...nextBranding };
    setBranding(merged);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    } catch (_) {}
  }, []);

  const refreshBranding = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/admin/branding`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        persistBranding({
          companyName: data.companyName ?? '',
          logoData: data.logoData ?? '',
          showCompanyLogo: data.showCompanyLogo !== false,
          updatedAt: data.updatedAt || null,
        });
      }
    } catch (err) {
      // Keep cached branding if backend is unreachable
      console.warn('Could not fetch system branding from server, using cached branding:', err);
    } finally {
      setLoading(false);
    }
  }, [persistBranding]);

  useEffect(() => {
    refreshBranding();
  }, [refreshBranding]);

  const updateBranding = useCallback(
    async (payload) => {
      const next = {
        companyName: payload.companyName ?? branding.companyName ?? '',
        logoData: payload.logoData ?? branding.logoData ?? '',
        showCompanyLogo: payload.showCompanyLogo !== undefined ? Boolean(payload.showCompanyLogo) : true,
      };
      // Optimistically persist locally so UI updates immediately
      persistBranding({ ...next, updatedAt: new Date().toISOString() });

      const response = await fetch(`${API_BASE_URL}/admin/branding`, {
        ...getAuthFetchOptions(),
        method: 'PUT',
        body: JSON.stringify(next),
      });

      if (response.ok) {
        const saved = await response.json();
        persistBranding(saved);
        return saved;
      }

      // If endpoint is not yet deployed on API Gateway (404/403), keep localStorage copy and still succeed gracefully
      if (response.status === 404) {
        return { ...next, updatedAt: new Date().toISOString() };
      }

      let message = 'Failed to save branding configuration';
      try {
        const errData = await response.json();
        message = errData.message || errData.error || message;
      } catch (_) {}
      throw new Error(message);
    },
    [branding, persistBranding]
  );

  return (
    <BrandingContext.Provider
      value={{
        branding,
        loading,
        refreshBranding,
        updateBranding,
      }}
    >
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding() {
  return useContext(BrandingContext);
}
